import React from 'react';
import { Container, Box } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { Button, useGlobal } from '@rcg/ui-react';
import { formatPrice } from '@rcg/pricing-format';
import useStyles from './index.styles';
import Picture from '../Picture';
import useDictionary from '../../hooks/useDictionary';
import Dictionary from '../../commons/dictionary';
import { ReactComponent as RightArrow } from '../../assets/svg/right-arrow.svg';
import CountdownClock from '../CountdownClock';
import { DEFAULT_ITINERARY_TIME, BOOKING_STATUS_BOOKED } from '../../commons/constants';
import { sendTrackingParams } from '../../commons/tracking/tracking';

const getItineraryName = (getDictionary, description) => {
  const itineraryTranslator = {
    nights: getDictionary(Dictionary.itineraryBookingAmendNights.key),
    night: getDictionary(Dictionary.itineraryBookingAmenNight.key),
    days: getDictionary(Dictionary.itineraryBookingAmendDays.key),
    day: getDictionary(Dictionary.itineraryBookingAmendDay.key),
  };
  return description.split(' ').reduce((previous, currentWord) => {
    return `${previous} ${itineraryTranslator[currentWord.toLowerCase()] || currentWord}`;
  }, '');
};

const Hero = ({
  images,
  isCourtesyHold,
  price,
  isTaBookingType,
  isGrpBookingType,
  renderPayment,
  urlPathStartPlanning,
  isBrasilMarket,
  itinerary,
  bookingStatus,
  sailDateUpdated,
  schedulePaymentData,
}) => {
  const classes = useStyles();
  const getDictionary = useDictionary(Dictionary);

  const {
    data: { localizedDayjs },
  } = useGlobal();

  const isBookedStatus = bookingStatus === BOOKING_STATUS_BOOKED;
  const schedulePayments =
    Boolean(schedulePaymentData?.payments) && schedulePaymentData?.payments[0]?.paymentSchedule;
  const isSchedulePayment = Boolean(schedulePayments);
  const { amount, scheduledDate } =
    (isSchedulePayment && schedulePayments.find((item) => item.status === 'PENDING')) || {};
  const formatedAmount = amount && formatPrice(amount, price?.currency, true, true, false);
  const nextPaymentDate = scheduledDate && localizedDayjs(scheduledDate).format('MMM DD, YYYY');
  const isBookingFullPayed = price?.balanceDue <= 0;
  const schedulePaymentLabel = `${getDictionary(
    Dictionary.heroSubtitleScheduledPayments.key,
  )} ${formatedAmount} ${getDictionary(
    Dictionary.heroSubtitleScheduledPaymentsLabel.key,
  )} ${nextPaymentDate}`;

  const getBannerDescriptionLabel = () => {
    if (isBrasilMarket && isCourtesyHold) {
      return (
        <>
          {getDictionary(Dictionary.heroSubtitleBrazilCourtesyHold.key)}
          <a href={`tel:${getDictionary(Dictionary.amendBrazilCallCenterNumber.key)}`}>
            {' '}
            {getDictionary(Dictionary.amendBrazilCallCenterNumber.key)}
          </a>
        </>
      );
    }
    if ((isTaBookingType || isGrpBookingType) && isCourtesyHold) {
      return getDictionary(Dictionary.heroBookingAmendTaCourtesyHoldDescription.key);
    }
    if (isBookingFullPayed || isBrasilMarket || isTaBookingType || isGrpBookingType) {
      return getDictionary(Dictionary.heroTitlePlanYourActivities.key);
    }
    if (!isBookingFullPayed && !scheduledDate && !isCourtesyHold) {
      return getDictionary(Dictionary.heroTitlePayYourBooking.key);
    }
    if (scheduledDate && !isBookingFullPayed) {
      return schedulePaymentLabel;
    }
    if (isCourtesyHold) {
      return getDictionary(Dictionary.heroTitleCompletePayment.key);
    }
    return null;
  };

  const makeRedirection = () => {
    sendTrackingParams(
      {
        eventType: 'interaction',
        interaction: 'viewUpgrade:Clicked',
      },
      false,
      isTaBookingType,
    );
    setTimeout(() => {
      window.location.href = urlPathStartPlanning;
    }, 1000);
  };

  const dispatchAction = () => {
    if (
      isBookingFullPayed ||
      (!isCourtesyHold && (isBrasilMarket || isTaBookingType || isGrpBookingType))
    ) {
      return makeRedirection();
    }
    return renderPayment();
  };

  const getNewDescription = () => {
    const { name, totalNights } = itinerary || {};
    return `${totalNights} Nights ${name}`;
  };

  const getDepartureTime = () =>
    itinerary?.days[0].ports[0]?.departureTime || DEFAULT_ITINERARY_TIME;

  const buildEndDate = () =>
    isBookedStatus
      ? localizedDayjs(`${sailDateUpdated}T${getDepartureTime()}`)
      : localizedDayjs([price.depositDueDate, 'T22:59:00'].join(''));

  const itineraryDescription = getItineraryName(getDictionary, getNewDescription());
  const showBannerLabel = (isBrasilMarket || isTaBookingType || isGrpBookingType) && isCourtesyHold;

  const getActionContent = () => (
    <Box className="container">
      <Typography component="h4">{getDictionary(Dictionary.amendHeroActionsTitle.key)}</Typography>
      <Typography>{getBannerDescriptionLabel()}</Typography>
    </Box>
  );

  return (
    <>
      <Container maxWidth={false} className={classes.hero}>
        <div className="gradientFilter" />
        <Picture
          className="heroImage"
          images={images}
          sizes="(max-width: 375px) 375px,
                (max-width: 768px) 768px,
                (max-width: 1024px) 1024px,
                1440px"
          defaultImage={images.length > 0 ? images[images.length - 1].url : ''}
        />
        {isCourtesyHold && (
          <Typography className="courtesy-hold">
            {getDictionary(Dictionary.amendHeroCourtesyHoldLabel.key)}
          </Typography>
        )}
        <Typography style={{ maxWidth: 340 }}>{itineraryDescription}</Typography>
        {itinerary && (
          <CountdownClock
            color="blue1"
            elevation={1}
            endDate={buildEndDate()}
            title={
              isBookedStatus
                ? getDictionary(Dictionary.countdownBookingAmendSetSail.key)
                : getDictionary(Dictionary.countdownBookingAmendCourtesyHold.key)
            }
            isCourtesyHold={isCourtesyHold}
          />
        )}
      </Container>

      {showBannerLabel ? (
        <Box className={classes.actions}>{getActionContent()}</Box>
      ) : (
        <Button className={`${classes.actions} ${classes.buttonHover}`} onClick={dispatchAction}>
          {getActionContent()}
          <RightArrow style={{ fill: 'white' }} />
        </Button>
      )}
    </>
  );
};
export default Hero;
